import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LuRefreshCw,
  LuLayers,
  LuContainer,
  LuZap,
  LuTriangle,
} from 'react-icons/lu';
import './PipelineStatus.css';
import TeamHeader from '../TeamHeader/TeamHeader';

// import exampleData from './example.json';

// Icons
interface SwarmService {
  service: string;
  status: string;
  replicas: string;
  image: string;
  last_event_time: string;
  last_error: string;
}

interface ContainerMetric {
  service: string;
  status: string;
  state: string;
  cpu_pct: number;
  mem_usage_mb: number;
  last_log: string;
  container_id: string;
  image: string;
}

interface StatusData {
  swarm: any[];
  heartbeat: {
    table_name: string;
    health_score: string;
    last_lcc_detected: string;
    last_classified_stroke: string;
    last_grouping_op: string;
    updated_at: string;
  };
  solutions: any[];
  containers: any[];
}

const PipelineStatus = () => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<StatusData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedService, setExpandedService] = useState<string | null>(null);

  // const [data, setData] = useState<StatusData>(exampleData as StatusData);
  // const [loading, setLoading] = useState<boolean>(false);
  // const fetchStatus = async () => {
  //   // For now, we just refresh the local state to simulate an update
  //   console.log('Simulating refresh for table:', tableName);
  //   setData({ ...exampleData } as StatusData);
  // };

  const [error, setError] = useState<string | null>(null);

  // Get the table from URL params, default to your specific table if missing
  const tableName =
    searchParams.get('table') || 'apache_solutions.usa_se_solutions';

  const fetchStatus = async () => {
    setLoading(true);
    try {
      // Adjust the URL to your actual API gateway/endpoint
      const response = await fetch(
        `pipelinemonitoring/status/?table=${tableName}`
      );
      if (!response.ok) throw new Error(`Error: ${response.statusText}`);

      const jsonData = await response.json();
      setData(jsonData);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Optional: Set up polling every 30 seconds for real-time monitoring
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [tableName]);

  const sortedContainers = useMemo(() => {
    if (!data?.containers) return [];
    return [...data.containers].sort((a, b) => b.cpu_pct - a.cpu_pct);
  }, [data?.containers]);

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const toggleService = (name: string) => {
    setExpandedService(expandedService === name ? null : name);
  };

  if (loading && !data)
    return (
      <div className="notification-hub-wrapper">
        <TeamHeader title="Pipeline Status" />
        <div className="loading-state">
          <LuRefreshCw className="spin" /> Loading System Status...
        </div>
      </div>
    );
  if (error)
    return (
      <div className="notification-hub-wrapper">
        <TeamHeader title="Pipeline Status" />
        <div className="error-state">
          <LuTriangle /> {error}
        </div>
      </div>
    );
  if (!data) return null;
  return (
    <div className="notification-hub-wrapper">
      <TeamHeader title="Pipeline Status" />

      <div className="alert-side-menu-and-body">
        <div className="pipeline-body">
          <div className="customer-title-and-expand-buttons">
            <h1 className="cutomer-title">Pipeline</h1>
          </div>
          <div className="dashboard-main-grid">
            {/* 1. Recent Solutions Feed */}
            <section className="status-section">
              <h2 className="dashboard-grid-title">
                <LuZap /> Recent Solutions
              </h2>
              <div className="status-card pulse-card">
                <h3>Monitoring Table</h3>
                <code>{data.heartbeat.table_name}</code>
              </div>
              <div className="solutions-feed">
                {data.solutions.map((sol: any) => (
                  <div key={sol.id} className="sol-item">
                    <strong>ID {sol.id}</strong>
                    <span>
                      Lat: {sol.ltg_lat.toFixed(3)} | Lon:{' '}
                      {sol.ltg_lon.toFixed(3)}
                    </span>
                    <span className="text-muted">
                      {new Date(sol.dttime_utc).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            </section>
            {/* 2. Heartbeat & Pulse */}
            <section className="status-section heartbeat-grid">
              <div className="status-card pulse-card">
                <h3>Monitoring Table</h3>
                <code>{data.heartbeat.table_name}</code>
              </div>
              <div className="status-card pulse-card">
                <h3>Last LCC Detection</h3>
                <p>
                  {new Date(data.heartbeat.last_lcc_detected).toLocaleString()}
                </p>
              </div>
              <div className="status-card pulse-card">
                <h3>Last Classified Stroke</h3>
                <p>
                  {new Date(
                    data.heartbeat.last_classified_stroke
                  ).toLocaleString()}
                </p>
              </div>
              <div className="status-card pulse-card">
                <h3>Last Grouped Stroke</h3>
                <p>
                  {new Date(data.heartbeat.last_grouping_op).toLocaleString()}
                </p>
              </div>
            </section>
          </div>

          <div className="dashboard-main-grid">
            {/* 3. Swarm Services */}
            <section className="status-section">
              <h2 className="dashboard-grid-title">
                <LuLayers /> Swarm Services
              </h2>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>Status</th>
                      <th>Replicas</th>
                      <th>Image</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.swarm.map((s: SwarmService, i: number) => {
                      const isExpanded = expandedService === s.service;

                      return (
                        <React.Fragment key={i}>
                          <tr
                            className={`clickable-row ${
                              s.status === 'DEGRADED' ? 'row-error' : ''
                            } ${isExpanded ? 'active-row' : ''}`}
                            onClick={() => toggleService(s.service)}
                          >
                            <td>{s.service}</td>
                            <td>
                              <span
                                className={`status-pill ${s.status.toLowerCase()}`}
                              >
                                {s.status}
                              </span>
                            </td>
                            <td>{s.replicas}</td>
                            <td className="text-muted">
                              {s.image.split('/').pop()}
                            </td>
                          </tr>

                          {/* Expandable Detail Row */}
                          {isExpanded && (
                            <tr className="expansion-row">
                              <td colSpan={4}>
                                <div className="expansion-content">
                                  <div className="expansion-grid">
                                    <div className="expansion-item">
                                      <strong>Last Event: </strong>
                                      <span>
                                        {new Date(
                                          s.last_event_time
                                        ).toLocaleString()}
                                      </span>
                                    </div>
                                    <div className="expansion-item">
                                      <strong>Full Image: </strong>
                                      <code className="text-muted">
                                        {s.image}
                                      </code>
                                    </div>
                                  </div>

                                  <div className="log-container error-box">
                                    <strong>Last Error: </strong>
                                    <code className="log-content error-text">
                                      {s.last_error ||
                                        'No errors reported for this service.'}
                                    </code>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            {/* 4. Containers Health */}
            <section className="status-section">
              <h2 className="dashboard-grid-title">
                <LuContainer /> Container Metrics
              </h2>
              <div className="metrics-grid">
                {sortedContainers.map((c: ContainerMetric, i: number) => {
                  const isExpanded = expandedId === c.container_id;

                  return (
                    <div
                      key={c.container_id || i}
                      className={`metric-tile clickable ${
                        isExpanded ? 'expanded' : ''
                      }`}
                      onClick={() => toggleExpand(c.container_id)}
                    >
                      <div className="metric-tile">
                        <span className="metric-name">
                          {c.service || 'Unnamed'}
                        </span>
                        <div className="metric-stats">
                          <span className={c.cpu_pct > 80 ? 'text-danger' : ''}>
                            CPU: {c.cpu_pct}%
                          </span>
                          <span>RAM: {c.mem_usage_mb.toFixed(1)} MB</span>
                        </div>
                        <div className={`mini-indicator ${c.state}`} />
                      </div>

                      {/* 2. Expandable Section */}
                      {isExpanded && (
                        <div className="metric-details-expanded">
                          <hr />
                          <div className="detail-row">
                            <strong>ID:</strong> <code>{c.container_id}</code>
                          </div>
                          <div className="detail-row">
                            <strong>Image:</strong> <span>{c.image}</span>
                          </div>
                          <div className="detail-row">
                            <strong>Status:</strong> <span>{c.status}</span>
                          </div>
                          <div className="log-preview">
                            <strong style={{ color: '#fff' }}>Last Log:</strong>
                            <pre>{c.last_log || 'No logs available'}</pre>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PipelineStatus;
